import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@jeraisonne/db';
import { isHomeworkDone, type CompletedAttemptRef } from '@/lib/student/homework';

type DoneCandidate = {
  id: string;
  exerciseId: string;
  status: 'pending' | 'in_progress' | 'completed';
  assignedAt?: string | null;
};

/**
 * Marque `completed` les devoirs dont l'exercice a été terminé depuis qu'ils
 * ont été donnés : `finish_attempt` ne met jamais `assignments.status` à jour
 * (le Feed fait le même constat). Lecture seule.
 */
export async function withDoneFromAttempts<T extends DoneCandidate>(
  supabase: SupabaseClient<Database>,
  studentId: string,
  items: T[],
): Promise<T[]> {
  const open = items.filter((i) => i.status !== 'completed');
  if (open.length === 0) return items;
  const { data } = await supabase
    .from('attempts')
    .select('exercise_id, completed_at')
    .eq('student_id', studentId)
    .eq('status', 'completed')
    .in('exercise_id', [...new Set(open.map((i) => i.exerciseId))]);
  const completed = (data ?? []) as CompletedAttemptRef[];
  return items.map((i) =>
    i.status !== 'completed' &&
    isHomeworkDone({ exerciseId: i.exerciseId, assignedAt: i.assignedAt ?? null }, completed)
      ? { ...i, status: 'completed' as const }
      : i,
  );
}
